import { useState, type FormEvent } from "react";
import { useSetK1, useTakeQuoteK1, type BoqDetail } from "../../api/boq";
import { formatMoney } from "../../api/money";

/** K1 on a hand-priced bill: the draft BOQ, or the customer BOQ copied from it.
 *
 * The rate card prices K1 at R0,00 -- it is a provisional sum, worked out per
 * job -- so it is typed here as an amount, and K2 follows it. On the customer
 * BOQ the approved quotation's full price is offered beside it; nothing moves
 * until the office takes it. Shown on no other bill: their K1 is worked out. */
export default function K1Panel({ detail }: { detail: BoqDetail }) {
  const set = useSetK1(detail.id);
  const take = useTakeQuoteK1(detail.id);
  const [amount, setAmount] = useState("");
  const a = detail.actions;
  const quote = detail.quote_k1;
  if (!a.can_set_k1 && quote === null) {
    return null;
  }
  const k1 = detail.lines.find((line) => line.payment_ref === "K1")?.line_total ?? null;

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (amount.trim() === "") {
      return;
    }
    set.mutate(amount.trim(), { onSuccess: () => setAmount("") });
  }

  return (
    <section className="cm-block" aria-label="K1, the provisional sum">
      <h3>K1, the provisional sum for the supply</h3>
      <p className="cm-doc-note">
        K1 is {k1 === null ? "not on the bill yet" : formatMoney(k1)}. K2 follows it as profit and attendance.
      </p>
      {quote !== null ? (
        quote.matches ? (
          <p className="notice">K1 is the approved quotation's full price, {formatMoney(quote.amount)}.</p>
        ) : (
          <div className="notice warn">
            <p>
              The approved quotation's full price is {formatMoney(quote.amount)}
              {k1 === null ? "" : `, against ${formatMoney(k1)} on this bill`}.
            </p>
            {a.can_take_quote_k1 ? (
              <button type="button" className="btn btn-primary" disabled={take.isPending} onClick={() => take.mutate()}>
                Use the quote's price
              </button>
            ) : null}
          </div>
        )
      ) : null}
      {a.can_set_k1 ? (
        <form className="cm-form" onSubmit={onSubmit} aria-label="Set K1">
          <label>
            K1 amount, excl VAT
            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 6 941,56"
              inputMode="decimal"
            />
          </label>
          <button type="submit" className="btn" disabled={set.isPending || amount.trim() === ""}>
            Set K1
          </button>
        </form>
      ) : null}
      {set.isError ? <p className="notice bad">{set.error.message}</p> : null}
      {take.isError ? <p className="notice bad">{take.error.message}</p> : null}
    </section>
  );
}
